// The tank family: TankGob (hull + independently-rotating turret, fire-when-facing with
// the +-3/16-tile spread = 2 sync-RNG draws per shot, idle turret pivots = 1 draw), and
// the four vehicles — LTank (plain shot), MTank (HALF damage x2: the second shot is
// pre-created un-launched (no gid yet!) and launched kcMTankSecondShot+GetUpdateCount()
// updates later), RTank (rocket, full damage), GTank (3-burst bullets at damage/3 via
// delayed kmidFire). game/Tank.cpp:1-601.

import type { SimContext } from '../context.ts';
import { MobileUnitGob, kwxInvalid, kfMuntFiring } from './mobileunit.ts';
import { UnitGob } from './unitgob.ts';
import {
  MobileUnitConsts,
  InitMobileUnitConstsClass,
  kfUntcNotifyEnemyNearby,
  kwcTile16th,
} from './unitconsts.ts';
import { kgtLightTank, kgtMediumTank, kgtRocketVehicle, kgtMachineGunVehicle } from './gobtypes.ts';
import {
  kutLightTank,
  kutMediumTank,
  kutRocketVehicle,
  kutMachineGunVehicle,
  kupgmAdvancedVTS,
} from '../player/side.ts';
import {
  Message,
  knHandled,
  kstReservedGlobal,
  kmidFire,
  kmidFireComplete,
} from '../sm/message.ts';
import {
  kfAniIgnoreFirstAdvance,
  kfAniResetWhenDone,
  kctUpdate,
  Animation,
} from '../anim/animation.ts';
import { CalcDir16, kdir16S } from '../math/dir.ts';
import { WcTrunc, WcFromPc32 } from '../math/coords.ts';
import { idiv } from '../math/int32.ts';
import {
  TankShotGob,
  CreateTankShotGob,
  CreateBulletGob,
  CreateRocketGob,
  kwcTankShotRate,
} from './shots.ts';
import { knLimitSupport } from './gobmgr.ts';
import { registerGobType } from './factory.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxNothing,
  ksfxLightTankFire,
  ksfxLightTankImpact,
  ksfxMediumTankFire,
  ksfxMediumTankImpact,
  ksfxRocketVehicleFire,
  ksfxRocketVehicleImpact,
  ksfxMachineGunVehicleFire,
  ksfxcVehicleDestroyed,
  ksfxcMale01Select,
  ksfxcMale01Move,
  ksfxcMale01Attack,
  ksfxcMale06Select,
  ksfxcMale06Move,
  ksfxcMale06Attack,
  ksfxcMajor01Select,
  ksfxcMajor01Move,
  ksfxcMajor01Attack,
  ksfxcMajor02Select,
  ksfxcMajor02Move,
  ksfxcMajor02Attack,
} from '../data/soundeffects.ts';
import type { Player } from '../player/player.ts';
import type { WPoint } from '../types.ts';

// game/Tank.cpp:6-7
export const kcMTankSecondShot = 2; // frame count of the first shot (anims must match!)
export const kifrmTankAction = 1; // the frame where the shot really starts

// game/Tank.cpp:259-261 — the shared strip tables (turret strips 16-31, base 0-15)
const s_anTurretStripIndices = [16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
const s_anBaseStripIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];

export class TankGob extends MobileUnitGob {
  m_dir16Turret: number;
  m_aniTurret = new Animation();

  // game/Tank.cpp:24-33 — hull spawns facing south; turret matches
  constructor(ctx: SimContext, pmuntc: MobileUnitConsts) {
    super(ctx, pmuntc);
    this.m_dir = kdir16S;
    this.m_dir16Turret = this.m_dir;
  }

  // game/Tank.cpp:35-44
  override Init(wx: number, wy: number, pplr: Player, fxHealth: number, ff: number): boolean {
    if (!super.Init(wx, wy, pplr, fxHealth, ff)) return false;

    this.StartAnimation(this.m_ani, this.m_pmuntc.anMovingStripIndices[this.m_dir], 0, 0);
    this.m_aniTurret.Init(this.ctx.animDataFor(this.m_pmuntc.szAniName));
    this.SetAnimationStrip(
      this.m_aniTurret,
      this.m_pmuntc.anFiringStripIndices[this.m_dir16Turret],
    );
    return true;
  }

  // game/Tank.cpp:102-143
  // game/Tank.cpp:48-66 (knVerTankGobState 1) — the turret re-acquires its strip
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_dir16Turret = pstm.ReadByte();
    this.m_aniTurret.Init(this.ctx.animDataFor(this.m_pmuntc.szAniName));
    this.SetAnimationStrip(
      this.m_aniTurret,
      this.m_pmuntc.anFiringStripIndices[this.m_dir16Turret],
    );
    return super.LoadState(pstm);
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteByte(this.m_dir16Turret);
    return super.SaveState(pstm);
  }

  override Fire(
    puntTarget: UnitGob | null,
    wx: number,
    wy: number,
    wdx: number,
    wdy: number,
  ): boolean {
    const dir16Fire = CalcDir16(wdx, wdy);
    if (this.m_dir16Turret !== dir16Fire) {
      this.m_unvl.MinSkip();
      return false;
    }

    const t = this.ctx.sim!.GetTickCount();
    const ctWait = this.m_pmuntc.ctFiringRate;
    const ctRemaining = ctWait - (t - this.m_tLastFire);
    if (ctRemaining > 0) {
      this.m_unvl.MinSkip(idiv(ctRemaining + idiv(kctUpdate, 2), kctUpdate) - 1);
      return false;
    }

    this.m_tLastFire = t;

    this.StartAnimation(
      this.m_aniTurret,
      this.m_pmuntc.anFiringStripIndices[dir16Fire],
      kifrmTankAction,
      kfAniIgnoreFirstAdvance | kfAniResetWhenDone,
    );
    this.m_wfMunt |= kfMuntFiring;
    this.ctx.smm!.SendDelayedMsgIds(
      kmidFireComplete,
      this.m_aniTurret.GetRemainingStripTime(),
      this.m_gid,
      this.m_gid,
    );

    // Fire off the shot! (the spread = 2 SYNC-RNG draws)
    const wdxRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const wdyRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const ptSpecial32 = this.m_aniTurret.GetSpecialPoint32(kifrmTankAction);

    this.LaunchProjectile(
      this.m_wx + WcFromPc32(ptSpecial32.x),
      this.m_wy + WcFromPc32(ptSpecial32.y),
      wx + wdxRnd,
      wy + wdyRnd,
      this.GetDamageTo(puntTarget!),
      this.m_gid,
      puntTarget!.GetId(),
    );

    this.ctx.host.PlaySfx(this.m_pmuntc.sfxFire);

    return true;
  }

  // game/Tank.cpp:146-149 — overridden by several tanks
  LaunchProjectile(
    wx: number,
    wy: number,
    wxTarget: number,
    wyTarget: number,
    nDamage: number,
    gidOwner: number,
    gidTarget: number,
  ): void {
    CreateTankShotGob(this.ctx, wx, wy, wxTarget, wyTarget, nDamage, gidOwner, gidTarget);
  }

  // game/Tank.cpp:151-165 — idle turret pivot (1 SYNC-RNG draw)
  override Idle(): void {
    if (this.ctx.rng.GetRandom() & 1) {
      this.m_dir16Turret--;
      if (this.m_dir16Turret < 0) this.m_dir16Turret = 15;
    } else {
      this.m_dir16Turret++;
      if (this.m_dir16Turret > 15) this.m_dir16Turret = 0;
    }
    this.SetAnimationStrip(
      this.m_aniTurret,
      this.m_pmuntc.anFiringStripIndices[this.m_dir16Turret],
    );
  }

  // game/Tank.cpp:167-208 — turret tracking (one 16th-step per update, shortest way round)
  override DefUpdate(): void {
    if (this.m_wptTarget.wx !== kwxInvalid && !(this.m_wfMunt & kfMuntFiring)) {
      // Aim at the unit's center if we have one, else m_wptTarget
      const wptAim: WPoint = { wx: this.m_wptTarget.wx, wy: this.m_wptTarget.wy };
      const pgobTarget = this.ctx.gobm.GetGob(this.m_gidTarget);
      if (pgobTarget !== null) pgobTarget.GetCenter(wptAim);

      // Same tile: stop trying to look exactly at it
      if (WcTrunc(this.m_wx) !== WcTrunc(wptAim.wx) || WcTrunc(this.m_wy) !== WcTrunc(wptAim.wy)) {
        const dir16 = CalcDir16(wptAim.wx - this.m_wx, wptAim.wy - this.m_wy);

        let d = dir16 - this.m_dir16Turret;
        if (d !== 0) {
          if (d < -8) d = 1;
          else if (d > 8) d = -1;
          if (d < 0) this.m_dir16Turret--;
          else this.m_dir16Turret++;
          this.m_dir16Turret = (this.m_dir16Turret >>> 0) % 16;
          this.SetAnimationStrip(
            this.m_aniTurret,
            this.m_pmuntc.anFiringStripIndices[this.m_dir16Turret],
          );
          this.m_unvl.MinSkip();
        }
      }
    }

    this.AdvanceAnimation(this.m_aniTurret);

    super.DefUpdate();
  }
}

// game/Tank.cpp:12-17 — the shared TankGob::InitClass: every tank wants enemy-nearby
// notification, then delegates to the MobileUnit consts parse
function InitTankConstsClass(ctx: SimContext, c: MobileUnitConsts, pini: IniReader): boolean {
  c.wf |= kfUntcNotifyEnemyNearby;
  return InitMobileUnitConstsClass(ctx, c, pini);
}

// game/Tank.cpp:248-296 — Light Tank
export function InitLTankClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtLightTank;
  c.ut = kutLightTank;
  c.anFiringStripIndices = s_anTurretStripIndices;
  c.anMovingStripIndices = s_anBaseStripIndices;
  c.anIdleStripIndices = s_anBaseStripIndices;
  // Sound effects (Tank.cpp:278-284)
  c.sfxFire = ksfxLightTankFire;
  c.sfxImpact = ksfxLightTankImpact;
  c.sfxcDestroyed = ksfxcVehicleDestroyed;
  c.sfxcSelect = ksfxcMajor02Select;
  c.sfxcMove = ksfxcMajor02Move;
  c.sfxcAttack = ksfxcMajor02Attack;
  return InitTankConstsClass(ctx, c, pini);
}

export class LTankGob extends TankGob {
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutLightTank] as MobileUnitConsts);
  }
}

// game/Tank.cpp:300-433 — Medium Tank: two half-damage shots, the second staged
export function InitMTankClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtMediumTank;
  c.ut = kutMediumTank;
  c.upgmPrerequisites = kupgmAdvancedVTS;
  c.anFiringStripIndices = s_anTurretStripIndices;
  c.anMovingStripIndices = s_anBaseStripIndices;
  c.anIdleStripIndices = s_anBaseStripIndices;
  // Sound effects (Tank.cpp:325-331)
  c.sfxFire = ksfxMediumTankFire;
  c.sfxImpact = ksfxMediumTankImpact;
  c.sfxcDestroyed = ksfxcVehicleDestroyed;
  c.sfxcSelect = ksfxcMale06Select;
  c.sfxcMove = ksfxcMale06Move;
  c.sfxcAttack = ksfxcMale06Attack;
  return InitTankConstsClass(ctx, c, pini);
}

export class MTankGob extends TankGob {
  m_pgobSecondShot: TankShotGob | null = null;
  m_cShotcountdown = 0;

  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutMediumTank] as MobileUnitConsts);
  }

  // issue #96/#98: the faithful C++ format omits the staged second shot (it has no gid, so it isn't
  // in the gob records) and its countdown; a from-scratch sim has them between the two shots and
  // the second's damage depends on it, so a COMPLETE snapshot serializes the staged shot INLINE.
  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    if (this.ctx.snapshotComplete) {
      const sh = this.m_pgobSecondShot;
      pstm.WriteByte(sh === null ? 0 : 1);
      if (sh !== null) {
        pstm.WriteWord(this.m_cShotcountdown & 0xffff);
        sh.m_li.SaveState(pstm);
        pstm.WriteWord(sh.m_gidOwner);
        pstm.WriteWord(sh.m_gidTarget);
        pstm.WriteWord(sh.m_nDamage);
        pstm.WriteWord(sh.m_wx);
        pstm.WriteWord(sh.m_wy);
        sh.m_ani.SaveState(pstm);
      }
    }
    return super.SaveState(pstm);
  }

  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    if (this.ctx.snapshotComplete) {
      if (pstm.ReadByte() !== 0) {
        this.m_cShotcountdown = (pstm.ReadWord() << 16) >> 16;
        const sh = new TankShotGob(this.ctx);
        sh.m_li.LoadState(pstm);
        sh.m_gidOwner = pstm.ReadWord();
        sh.m_gidTarget = pstm.ReadWord();
        sh.m_nDamage = pstm.ReadWord();
        sh.m_wx = (pstm.ReadWord() << 16) >> 16;
        sh.m_wy = (pstm.ReadWord() << 16) >> 16;
        sh.m_ani.Init(this.ctx.animDataFor('tankshot.anir'));
        sh.m_ani.LoadState(pstm);
        this.m_pgobSecondShot = sh;
      }
    }
    return super.LoadState(pstm);
  }

  // game/Tank.cpp:330-336 — abandon a staged shot (it never had a gid)
  override Deactivate(): void {
    this.m_pgobSecondShot = null;
    super.Deactivate();
  }

  // game/Tank.cpp:360-400 — first shot now; the second pre-created UN-LAUNCHED (no gid
  // until Launch) at the muzzle offset between action frames
  override LaunchProjectile(
    wx: number,
    wy: number,
    wxTarget: number,
    wyTarget: number,
    nDamage: number,
    gidOwner: number,
    gidTarget: number,
  ): void {
    CreateTankShotGob(this.ctx, wx, wy, wxTarget, wyTarget, idiv(nDamage, 2), gidOwner, gidTarget);
    this.m_cShotcountdown = kcMTankSecondShot;
    this.m_cShotcountdown += this.m_unvl.GetUpdateCount();
    this.m_unvl.MinSkip(this.m_cShotcountdown);
    if (this.ctx.gobm.IsBelowLimit(knLimitSupport)) {
      const pgob = new TankShotGob(this.ctx);
      const ptSpecial32a = this.m_aniTurret.GetSpecialPoint32(kifrmTankAction);
      const ptSpecial32b = this.m_aniTurret.GetSpecialPoint32(kifrmTankAction + kcMTankSecondShot);
      const dx = WcFromPc32(ptSpecial32b.x - ptSpecial32a.x);
      const dy = WcFromPc32(ptSpecial32b.y - ptSpecial32a.y);
      if (
        pgob.InitShot(
          this.ctx.animDataFor('tankshot.anir'),
          wx + dx,
          wy + dy,
          wxTarget + dx,
          wyTarget + dy,
          idiv(nDamage, 2),
          gidOwner,
          gidTarget,
          kwcTankShotRate,
        )
      ) {
        this.m_pgobSecondShot = pgob;
      }
    }
  }

  // game/Tank.cpp:402-419
  override DefUpdate(): void {
    if (this.m_pgobSecondShot !== null) {
      this.m_cShotcountdown -= this.m_unvl.GetUpdateCount();
      if (this.m_cShotcountdown < 0) {
        if (this.ctx.gobm.IsBelowLimit(knLimitSupport)) {
          this.m_pgobSecondShot.Launch();
        }
        this.m_pgobSecondShot = null;
        this.ctx.host.PlaySfx(this.m_pmuntc.sfxFire);
      }
    }
    super.DefUpdate();
  }
}

// game/Tank.cpp:437-475 — Rocket Vehicle: full-damage rocket
export function InitRTankClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtRocketVehicle;
  c.ut = kutRocketVehicle;
  c.upgmPrerequisites = kupgmAdvancedVTS;
  c.anFiringStripIndices = s_anTurretStripIndices;
  c.anMovingStripIndices = s_anBaseStripIndices;
  c.anIdleStripIndices = s_anBaseStripIndices;
  // Sound effects (Tank.cpp:452-458)
  c.sfxFire = ksfxRocketVehicleFire;
  c.sfxImpact = ksfxRocketVehicleImpact;
  c.sfxcDestroyed = ksfxcVehicleDestroyed;
  c.sfxcSelect = ksfxcMajor01Select;
  c.sfxcMove = ksfxcMajor01Move;
  c.sfxcAttack = ksfxcMajor01Attack;
  return InitTankConstsClass(ctx, c, pini);
}

export class RTankGob extends TankGob {
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutRocketVehicle] as MobileUnitConsts);
  }

  // game/Tank.cpp:470-474
  override LaunchProjectile(
    wx: number,
    wy: number,
    wxTarget: number,
    wyTarget: number,
    nDamage: number,
    gidOwner: number,
    gidTarget: number,
  ): void {
    CreateRocketGob(this.ctx, wx, wy, wxTarget, wyTarget, nDamage, gidOwner, gidTarget);
  }
}

// game/Tank.cpp:479-600 — Machine Gun Vehicle: 3-burst bullets at damage/3
export function InitGTankClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtMachineGunVehicle;
  c.ut = kutMachineGunVehicle;
  c.anFiringStripIndices = s_anTurretStripIndices;
  c.anMovingStripIndices = s_anBaseStripIndices;
  c.anIdleStripIndices = s_anBaseStripIndices;
  // Sound effects (Tank.cpp:504-510)
  c.sfxFire = ksfxMachineGunVehicleFire;
  c.sfxImpact = ksfxNothing;
  c.sfxcDestroyed = ksfxcVehicleDestroyed;
  c.sfxcSelect = ksfxcMale01Select;
  c.sfxcMove = ksfxcMale01Move;
  c.sfxcAttack = ksfxcMale01Attack;
  return InitTankConstsClass(ctx, c, pini);
}

export class GTankGob extends TankGob {
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutMachineGunVehicle] as MobileUnitConsts);
  }

  // game/Tank.cpp:505-540 — NOTE: rate check FIRST, then facing (unlike TankGob::Fire)
  override Fire(
    puntTarget: UnitGob | null,
    wx: number,
    wy: number,
    wdx: number,
    wdy: number,
  ): boolean {
    const t = this.ctx.sim!.GetTickCount();
    const ctWait = this.m_pmuntc.ctFiringRate;
    const ctRemaining = ctWait - (t - this.m_tLastFire);
    if (ctRemaining > 0) {
      this.m_unvl.MinSkip(idiv(ctRemaining + idiv(kctUpdate, 2), kctUpdate) - 1);
      return false;
    }

    const dir16Fire = CalcDir16(wdx, wdy);
    if (this.m_dir16Turret !== dir16Fire) return false;

    this.m_tLastFire = t;

    this.StartAnimation(
      this.m_aniTurret,
      this.m_pmuntc.anFiringStripIndices[dir16Fire],
      0,
      kfAniResetWhenDone,
    );
    this.m_wfMunt |= kfMuntFiring;
    this.ctx.smm!.SendDelayedMsgIds(
      kmidFireComplete,
      this.m_aniTurret.GetRemainingStripTime(),
      this.m_gid,
      this.m_gid,
    );

    const wdxRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const wdyRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const ptSpecial32 = this.m_aniTurret.GetSpecialPoint32(kifrmTankAction);
    CreateBulletGob(
      this.ctx,
      this.m_wx + WcFromPc32(ptSpecial32.x),
      this.m_wy + WcFromPc32(ptSpecial32.y),
      wx + wdxRnd,
      wy + wdyRnd,
      idiv(this.GetDamageTo(puntTarget!), 3),
      this.m_gid,
      puntTarget!.GetId(),
    );

    // Two more shots at slight delays
    this.ctx.smm!.SendDelayedMsgIds(kmidFire, kctUpdate * 3, this.m_gid, this.m_gid);
    this.ctx.smm!.SendDelayedMsgIds(kmidFire, kctUpdate * 6, this.m_gid, this.m_gid);

    this.ctx.host.PlaySfx(this.m_pmuntc.sfxFire);

    return true;
  }

  // game/Tank.cpp:542-600 — the burst follow-ups
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal && pmsg.mid === kmidFire) {
      const puntTarget = this.ctx.gobm.GetGob(this.m_gidTarget) as UnitGob | null;
      if (puntTarget !== null) {
        const wdxRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
        const wdyRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
        const wpt: WPoint = { wx: 0, wy: 0 };
        puntTarget.GetCenter(wpt);
        const ptSpecial32 = this.m_aniTurret.GetSpecialPoint32(kifrmTankAction);
        CreateBulletGob(
          this.ctx,
          this.m_wx + WcFromPc32(ptSpecial32.x),
          this.m_wy + WcFromPc32(ptSpecial32.y),
          wpt.wx + wdxRnd,
          wpt.wy + wdyRnd,
          idiv(this.GetDamageTo(puntTarget), 3),
          this.m_gid,
          puntTarget.GetId(),
        );
      }
      return knHandled;
    }
    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// self-register (see factory.ts)
registerGobType(kgtLightTank, (ctx) => new LTankGob(ctx));
registerGobType(kgtMediumTank, (ctx) => new MTankGob(ctx));
registerGobType(kgtRocketVehicle, (ctx) => new RTankGob(ctx));
registerGobType(kgtMachineGunVehicle, (ctx) => new GTankGob(ctx));
